import { FC } from 'react';
import Star, { StarFill } from './Star';
import {
  RatingWrapper,
  Average,
  StarsRow,
  ReviewsText,
  ReviewsLink,
} from './styles';

export interface RatingProps {
  value: number;
  reviewCount: number;
  /** If true, renders the numeric average alongside the stars. */
  showAverage?: boolean;
  /** If provided, the reviews count renders as an anchor pointing to this href (e.g. "#reviews"). */
  reviewsHref?: string;
  /** Optional click handler for if the user clicks on the anchor tag */
  onClick?: () => void;
}

export const valueToStarFills = (value: number): StarFill[] => {
  const clamped = Math.min(5, Math.max(0, value));
  const floor = Math.floor(clamped);
  const frac = clamped - floor;
  const fullCount = frac > 0.5 ? floor + 1 : floor;
  const hasHalf = frac > 0 && frac <= 0.5;

  return Array.from({ length: 5 }, (_, i) => {
    if (i < fullCount) return 'full';
    if (i === fullCount && hasHalf) return 'half';
    return 'empty';
  });
};

const formatAverage = (value: number): string => {
  const clamped = Math.min(5, Math.max(0, value));
  const rounded = Math.round(clamped * 10) / 10;
  return rounded.toString();
};

const Rating: FC<RatingProps> = ({
  value,
  reviewCount,
  showAverage,
  reviewsHref,
  onClick,
}) => {
  const fills = valueToStarFills(value);
  const average = formatAverage(value);
  const ariaLabel = `Rated ${average} out of 5 stars from ${reviewCount} reviews`;

  const reviewsText = `${reviewCount} Reviews`;

  return (
    <RatingWrapper role='img' aria-label={ariaLabel}>
      {showAverage && <Average aria-hidden={true}>{average}</Average>}
      <StarsRow aria-hidden={true}>
        {fills.map((fill, i) => (
          <Star key={i} fill={fill} />
        ))}
      </StarsRow>
      {reviewsHref ? (
        <ReviewsLink onClick={onClick} href={reviewsHref}>
          {reviewsText}
        </ReviewsLink>
      ) : (
        <ReviewsText aria-hidden={true}>{reviewsText}</ReviewsText>
      )}
    </RatingWrapper>
  );
};

export default Rating;
